import {
  ref, reactive, computed, onMounted,
} from '@vue/composition-api'
import organization from '@/http/requests/organization'
import userRequest from '@/http/requests/user'
import userTypes from '@/auth/type'
import { parseRequestError } from '@/helpers/helpers'
import { required, email, password } from '@validations'
import store from '@/store'

export default (props, { root, emit }) => {
  const confirmForm = ref()
  const isLoggedIn = computed(() => root.$store.state.auth.isUserLoggedIn())
  const user = computed(() => root.$store.state.auth.activeUser)
  const isGuardian = computed(() => isLoggedIn.value && user.value.type === userTypes.guardian)

  const customers = ref([])
  const selectedCustomerId = ref(null)

  // Inline login support
  const showLogin = ref(false)
  const loginEmail = ref('')
  const loginPassword = ref('')
  const loginProcessing = ref(false)
  const loginForm = ref()
  const accountExistsMessage = ref('')

  const fetchCustomerData = () => {
    if (isGuardian.value) {
      userRequest
        .getMyOrganizationCustomers(props.selectedService.organization.id)
        .then(res => {
          if (!Array.isArray(res.data) || res.data.length === 0) {
            return
          }
          customers.value = res.data
          selectedCustomerId.value = res.data[0].id
        })
        .catch(err => parseRequestError(err))
    } else if (isLoggedIn.value) {
      userRequest
        .getMyOrganizationCustomer(props.selectedService.organization.id)
        .then(res => {
          selectedCustomerId.value = res.data.id
        })
        .catch(err => parseRequestError(err))
    }
  }

  onMounted(() => {
    fetchCustomerData()
  })

  const guest = reactive({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
  })

  const acceptRegulations = ref(false)
  const phoneNumberValid = ref(null)
  const phoneTouched = ref(false)
  const processing = ref(false)

  const handlePhoneValidation = phoneObject => {
    const raw = (phoneObject && (phoneObject.formatted || phoneObject.number || phoneObject.phone)) || ''
    const value = String(raw).trim()
    const hasInput = value.length > 0

    if (hasInput && !phoneTouched.value) {
      phoneTouched.value = true
    }

    // Puste – nie oceniamy (required pokaże się po touched dzięki mode="lazy")
    if (!hasInput) {
      phoneNumberValid.value = null
      return
    }

    phoneNumberValid.value = !!(phoneObject && phoneObject.valid)
  }

  const confirmReservation = () => {
    confirmForm.value.validate().then(valid => {
      if (!isLoggedIn.value && (!valid || !phoneNumberValid.value || !acceptRegulations.value)) return

      const customersPayload = []
      if (isLoggedIn.value) {
        customersPayload.push({ id: selectedCustomerId.value })
      } else {
        const data = {
          firstName: guest.firstName,
          lastName: guest.lastName,
          email: guest.email,
          phone: guest.phone,
          password: guest.password,
        }
        customersPayload.push(data)
      }

      const calendarId = props.selectedCalendarId
        || props.selectedService.calendar?.id
        || props.selectedService.calendarId

      const location = props.selectedLocation
        || (Array.isArray(props.selectedService.serviceLocation) ? props.selectedService.serviceLocation[0] : props.selectedService.serviceLocation)

      const spotsPayload = props.selectedSpots.map(spot => {
        const payload = {}

        if (spot.calendarEventId) {
          payload.calendarEventId = spot.calendarEventId
        } else if (spot.startAt) {
          payload.from = spot.startAt
        }

        if (spot.customerId) {
          payload.customerId = spot.customerId
        }

        payload.calendarId = spot.calendarId || calendarId

        return payload
      })

      const payload = {
        calendarId,
        serviceId: props.selectedService.id,
        location,
        tz: props.tz,
        customers: customersPayload,
        spots: spotsPayload,
      }

      if (!payload.calendarId || !payload.location) {
        return
      }

      processing.value = true
      organization.book(payload)
        .then(() => {
          emit('confirmed')
          store.commit('publicStore/SET_RESERVATION_SELECTED_SPOTS', [])
        })
        .catch(err => {
          const status = err?.response?.status || err?.status || err?.code
          if (status === 409) {
            showLogin.value = true
            accountExistsMessage.value = root.$t('auth.login.account-exists')
            if (!loginEmail.value && guest.email) loginEmail.value = guest.email
          } else {
            parseRequestError(err)
          }
        })
        .finally(() => { processing.value = false })
    })
  }

  const login = () => {
    if (isLoggedIn.value) return
    loginForm.value.validate().then(valid => {
      if (!valid) return
      loginProcessing.value = true
      const payload = { email: loginEmail.value, password: loginPassword.value }
      root.$store.dispatch('auth/login', payload)
        .then(() => root.$store.dispatch('auth/fetchUserInfo'))
        .catch(err => {
          const status = err?.response?.status || err?.status || err?.code
          if (status !== 409) {
            const msg = (typeof err?.message === 'string') ? err.message : 'auth.login.failed'
            if (root.$t) window.toast.notify.danger(root.$t(msg))
          }
        })
        .finally(() => {
          showLogin.value = false
          loginProcessing.value = false
          fetchCustomerData()
        })
    })
  }

  const loginButtonClick = () => {
    if (!showLogin.value) {
      showLogin.value = true
      if (!loginEmail.value && guest.email) loginEmail.value = guest.email
      return
    }
    login()
  }

  return {
    confirmForm,
    isLoggedIn,
    isGuardian,
    customers,
    selectedCustomerId,
    guest,
    acceptRegulations,
    phoneNumberValid,
    phoneTouched,
    processing,
    // login inline state
    showLogin,
    loginEmail,
    loginPassword,
    loginProcessing,
    loginForm,
    accountExistsMessage,
    // methods
    handlePhoneValidation,
    confirmReservation,
    login,
    loginButtonClick,
    required,
    email,
    password,
  }
}
